import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { TdDialogService } from '@covalent/core/dialogs';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { map, share, switchMap, take, tap } from 'rxjs/operators';
import { EmploymentRecordHttpClient } from 'src/app/shared/flight-log-api/employment-records/employment-records-client.service';
import { FlLoadingService } from 'src/lib/fl-loading/fl-loading.service';
import { sleep } from 'src/util/async';
import {
  createPrdSeparationTypes,
  EmploymentRecordViewModel,
} from 'src/view-models/employment-record-view-model';
import { PilotEmploymentRecordAddDialogComponent } from '../pilot-employment-record-add-dialog/pilot-employment-record-add-dialog.component';
import { PilotEmploymentRecordEditDialogComponent } from '../pilot-employment-record-edit-dialog/pilot-employment-record-edit-dialog.component';

@Component({
  selector: 'fl-pilot-employment-records-list',
  templateUrl: './pilot-employment-records-list.component.html',
  styleUrls: ['./pilot-employment-records-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*', padding: '0 0 25px 0' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class PilotEmploymentRecordsListComponent implements OnInit, OnDestroy {
  @Input()
  public pilotId: string;

  public employmentRecords$: Observable<EmploymentRecordViewModel[]>;

  public displayedColumns = [
    'prdDateOfHire',
    'completeDate',
    'finalRecordsDate',
    'separationDate',
    'separationType',
    'separationTypeDetail',
    'relevantDocuments',
    'processingState',
    'processingError',
    'lastProcessedDate',
    'prdSourceSystemId',
    'actions',
  ];

  public displayedColumnsWithExpand = [...this.displayedColumns, 'expand'];
  public expandedEmploymentRecord: EmploymentRecordViewModel | null;

  public _pageRefresh$ = new BehaviorSubject<{}>({});
  public _onDestroy$ = new Subject<void>();

  public loadingKey = 'pilot-employment-records-table';

  public total$: Observable<number>;
  public defaultPageSize = 5;
  public pageSizeOptions = [this.defaultPageSize, 10, 25];
  public page$ = new BehaviorSubject<{ take: number; skip: number }>({
    take: this.defaultPageSize,
    skip: 0,
  });

  constructor(
    private _employmentRecord: EmploymentRecordHttpClient,
    private _loading: FlLoadingService,
    private _dialogService: TdDialogService,
    private _dialog: MatDialog,
    private _snackBar: MatSnackBar,
  ) {
    const employmentRecordResults$ = combineLatest(this._pageRefresh$).pipe(
      switchMap(() => {
        this.markAsLoading();
        return this._employmentRecord.getByPilotId({
          pilotId: this.pilotId,
        });
      }),
      tap(() => this.markAsLoaded()),
      share(),
    );

    this.employmentRecords$ = combineLatest([employmentRecordResults$, this.page$]).pipe(
      map(([r, page]) =>
        r.results
          .sort((a, b) => new Date(a.prdDateOfHire).getTime() - new Date(b.prdDateOfHire).getTime())
          .slice(page.skip, page.skip + page.take),
      ),
    );

    this.total$ = employmentRecordResults$.pipe(map((r) => r.totalCount));
  }

  public ngOnInit() {}

  public ngOnDestroy() {
    this._onDestroy$.next();
  }

  public markAsLoading() {
    this._loading.register(this.loadingKey);
  }

  public markAsLoaded() {
    this._loading.resolveAll(this.loadingKey);
  }

  public edit(employmentRecord: EmploymentRecordViewModel) {
    const dialogRef = this._dialog.open(PilotEmploymentRecordEditDialogComponent, {
      data: {
        pilotId: this.pilotId,
        employmentRecord,
      },
      disableClose: true,
    });
    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(async () => {
        this.markAsLoading();
        await sleep(2000);
        this._pageRefresh$.next({});
      });
  }

  public async delete(employementRecordId: string) {
    const result = await this._dialogService
      .openConfirm({
        title: 'Delete Pilot Employment Record?',
        message: 'Are you sure you wish to delete this pilot employment record?',
        acceptButton: 'Delete',
        cancelButton: 'Cancel',
      })
      .afterClosed()
      .toPromise();
    if (result === true) {
      try {
        await this._employmentRecord.delete(employementRecordId).toPromise();
        this.markAsLoading();
        await sleep(2000);
        this._snackBar.open('Succesfully deleted pilot employment record.', undefined, {
          duration: 2000,
        });
        this._pageRefresh$.next({});
      } catch (err) {
        this._snackBar.open('Error deleting pilot employment record.', undefined, {
          duration: 2000,
        });

        throw err;
      }
    }
  }

  public add() {
    const dialogRef = this._dialog.open(PilotEmploymentRecordAddDialogComponent, {
      data: {
        pilotId: this.pilotId,
      },
      disableClose: true,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(async () => {
        this.markAsLoading();
        await sleep(2000);
        this._pageRefresh$.next({});
      });
  }

  public changeExpandedRecord(employmentRecord: EmploymentRecordViewModel) {
    if (employmentRecord === this.expandedEmploymentRecord) {
      this.expandedEmploymentRecord = null;
    } else {
      this.expandedEmploymentRecord = employmentRecord;
    }
  }

  public refresh() {
    this._pageRefresh$.next({});
  }

  public getSeparationTypeDescription(code: any) {
    return createPrdSeparationTypes().codeToDescription(code);
  }

  public onPage(page: PageEvent) {
    const skip = page.pageIndex * page.pageSize;
    this.page$.next({ skip, take: page.pageSize });
  }
}
